// The Namat scale (spec #86, ADR 0002): a beneficiary's behaviours (the
// questionnaire's questions) and three of their results in the body (BMI,
// blood pressure, HbA1c), each scored 0-3, shown as one percentage with
// compliance beside it. Every screen shows what this returns and computes
// nothing itself. Children 2-19 are judged by CDC BMI-for-age percentiles and
// blood pressure is left out under 13 (#90); age is taken at the exam date.

import { CDC_BMI_BOYS, CDC_BMI_GIRLS, type CdcBmiRow } from './cdcBmiForAge';
import { fitnessAge } from './fitnessAge';

export const VALIDITY_DAYS_DEFAULT = 90;
const DAY_MS = 86_400_000;

export type Outcome = 'bmi' | 'bloodPressure' | 'hba1c';
export const OUTCOMES: Outcome[] = ['bmi', 'bloodPressure', 'hba1c'];

export type Band = 'excellent' | 'very_good' | 'good' | 'needs_improvement';

export interface ResponseInput {
  completedAt: Date;
  answers: { questionId: string; score: number }[];
}

export interface ExamInput {
  date: Date;
  status?: string;
  bmi?: unknown;
  bloodPressure?: { systolic?: unknown; diastolic?: unknown };
  hba1c?: unknown;
}

export type Sex = 'male' | 'female';

export interface PersonInput {
  birthDate?: Date | null;
  sex?: Sex | null;
}

export type BmiCategory = 'underweight' | 'healthy' | 'overweight' | 'obese' | 'severe_obesity';

export interface ScaleItem {
  key: string; // question id or outcome
  kind: 'question' | 'outcome';
  score: number;
  max: number;
}

export interface NamatScale {
  score: number;
  max: number;
  percent: number;
  band: Band;
  compliance: { met: number; of: number };
  items: ScaleItem[];
  missing: Outcome[];
  /** Outcomes that do not apply to this person (blood pressure under 13). */
  excluded: Outcome[];
}

export interface NamatAssessment {
  status: 'valid' | 'expired' | 'not_done';
  filledAt: Date | null;
  expiresAt: Date | null;
  /** Computed for valid and expired results; expired ones are shown greyed. */
  scale: NamatScale | null;
  /** A completed exam exists within the validity window. */
  examDone: boolean;
}

/** Exam values arrive as numbers or strings; 0, empty or missing means "not measured". */
const measured = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Standard normal cumulative distribution (Abramowitz-Stegun 7.1.26 via erf). */
function normalCdf(z: number): number {
  const t = 1 / (1 + 0.3275911 * Math.abs(z / Math.SQRT2));
  const erf = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-(z * z) / 2);
  return z >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
}

/** Whole months between two dates. */
export function ageInMonths(birthDate: Date, at: Date): number {
  let months = (at.getUTCFullYear() - birthDate.getUTCFullYear()) * 12 + (at.getUTCMonth() - birthDate.getUTCMonth());
  if (at.getUTCDate() < birthDate.getUTCDate()) months -= 1;
  return months;
}

/**
 * BMI category. From 2 up to 20 years it follows the CDC BMI-for-age
 * percentile for the sex (severe obesity = at least 120% of the 95th
 * percentile); from 20, or when the age is unknown, the adult ranges.
 * Returns a null category under 2. A child without a recorded sex is judged
 * as DEFAULT_CHILD_SEX.
 */
/** All child beneficiaries are boys for now (2026-10-02); used when a child's sex is not recorded. */
export const DEFAULT_CHILD_SEX: Sex = 'male';

export function bmiCategory(
  bmi: number,
  ageMonths: number | null,
  sex: Sex | null | undefined
): { category: BmiCategory | null; percentile: number | null } {
  if (ageMonths !== null && ageMonths < 24) return { category: null, percentile: null };
  if (ageMonths !== null && ageMonths < 240) {
    const table: CdcBmiRow[] = (sex || DEFAULT_CHILD_SEX) === 'male' ? CDC_BMI_BOYS : CDC_BMI_GIRLS;
    const target = ageMonths + 0.5;
    const row = table.reduce((found, r) => (r[0] <= target ? r : found), table[0]);
    const [, L, M, S, p5, p85, p95] = row;
    const z = L === 0 ? Math.log(bmi / M) / S : (Math.pow(bmi / M, L) - 1) / (L * S);
    const percentile = Math.round(normalCdf(z) * 100);
    const category: BmiCategory =
      bmi >= 1.2 * p95 ? 'severe_obesity' : bmi >= p95 ? 'obese' : bmi >= p85 ? 'overweight' : bmi < p5 ? 'underweight' : 'healthy';
    return { category, percentile };
  }
  const category: BmiCategory =
    bmi >= 35 ? 'severe_obesity' : bmi >= 30 ? 'obese' : bmi >= 25 ? 'overweight' : bmi < 18.5 ? 'underweight' : 'healthy';
  return { category, percentile: null };
}

const BMI_SCORE: Record<BmiCategory, number> = { healthy: 3, underweight: 2, overweight: 2, obese: 1, severe_obesity: 0 };

/** 3/2/1/0 per outcome, or null when not measured. Proposed cut-offs, to be confirmed by the clinical lead (spec #86). */
export function outcomeScore(
  outcome: Outcome,
  exam: Omit<ExamInput, 'date'>,
  person?: { ageMonths: number | null; sex?: Sex | null }
): number | null {
  if (outcome === 'bmi') {
    const bmi = measured(exam.bmi);
    if (bmi === null) return null;
    const { category } = bmiCategory(bmi, person?.ageMonths ?? null, person?.sex);
    return category === null ? null : BMI_SCORE[category];
  }
  if (outcome === 'bloodPressure') {
    const s = measured(exam.bloodPressure?.systolic);
    const d = measured(exam.bloodPressure?.diastolic);
    if (s === null || d === null) return null;
    if (s >= 140 || d >= 90) return 0;
    if (s >= 130 || d >= 80) return 1;
    if (s >= 120) return 2;
    return 3;
  }
  const a1c = measured(exam.hba1c);
  if (a1c === null) return null;
  if (a1c >= 8) return 0;
  if (a1c >= 6.5) return 1;
  if (a1c >= 5.7) return 2;
  return 3;
}

export function bandOf(percent: number): Band {
  if (percent >= 80) return 'excellent';
  if (percent >= 70) return 'very_good';
  if (percent >= 60) return 'good';
  return 'needs_improvement';
}

export function assessNamat(input: {
  response: ResponseInput | null;
  exams: ExamInput[];
  now: Date;
  validityDays?: number;
  /** Highest option score per question; defaults to 3 (both live questionnaires). */
  maxScoreByQuestion?: Record<string, number>;
  /** Birth date and sex; without a birth date adult ranges apply. */
  person?: PersonInput;
}): NamatAssessment {
  const validityMs = (input.validityDays ?? VALIDITY_DAYS_DEFAULT) * DAY_MS;
  const windowStart = input.now.getTime() - validityMs;
  const exam = input.exams
    .filter((e) => e.status === 'completed' && e.date.getTime() >= windowStart && e.date.getTime() <= input.now.getTime())
    .sort((a, b) => b.date.getTime() - a.date.getTime())[0];
  const examDone = !!exam;

  const response = input.response;
  if (!response || response.answers.length === 0) {
    return { status: 'not_done', filledAt: null, expiresAt: null, scale: null, examDone };
  }
  const expiresAt = new Date(response.completedAt.getTime() + validityMs);
  const status = expiresAt.getTime() < input.now.getTime() ? 'expired' : 'valid';

  const items: ScaleItem[] = response.answers.map((a) => ({
    key: a.questionId,
    kind: 'question',
    score: a.score,
    max: input.maxScoreByQuestion?.[a.questionId] ?? 3,
  }));
  const missing: Outcome[] = [];
  const excluded: Outcome[] = [];
  const birthDate = input.person?.birthDate ?? null;
  const ageMonths = birthDate ? ageInMonths(birthDate, exam ? exam.date : input.now) : null;
  for (const outcome of OUTCOMES) {
    if (outcome === 'bloodPressure' && ageMonths !== null && ageMonths < 13 * 12) {
      excluded.push(outcome);
      continue;
    }
    const score = exam ? outcomeScore(outcome, exam, { ageMonths, sex: input.person?.sex }) : null;
    if (score === null) missing.push(outcome);
    else items.push({ key: outcome, kind: 'outcome', score, max: 3 });
  }

  const score = items.reduce((s, i) => s + i.score, 0);
  const max = items.reduce((s, i) => s + i.max, 0);
  const percent = max > 0 ? Math.round((score / max) * 100) : 0;
  return {
    status,
    filledAt: response.completedAt,
    expiresAt,
    examDone,
    scale: {
      score,
      max,
      percent,
      band: bandOf(percent),
      compliance: { met: items.filter((i) => i.score >= i.max).length, of: items.length },
      items,
      missing,
      excluded,
    },
  };
}

// ---------------------------------------------------------------------------
// Health indicators (#91): each shown on its own in three levels to the
// examiner, doctor, admin and the beneficiary; never to the manager. Limits
// are the current ones, to be confirmed by the clinical lead (spec #86); change
// them here only.

export type Indicator =
  | 'bmi'
  | 'bloodPressure'
  | 'hba1c'
  | 'cholesterol'
  | 'pulse'
  | 'waistCircumference'
  | 'neckCircumference'
  | 'fitnessAge';
export const INDICATORS: Indicator[] = [
  'bmi',
  'bloodPressure',
  'hba1c',
  'cholesterol',
  'pulse',
  'waistCircumference',
  'neckCircumference',
  'fitnessAge',
];
export type Level = 'good' | 'acceptable' | 'bad';

export const INDICATOR_LIMITS = {
  /** acceptable from either value, bad from either value (mmHg) */
  bloodPressure: { acceptable: { systolic: 120, diastolic: 80 }, bad: { systolic: 140, diastolic: 90 } },
  /** % */
  hba1c: { acceptable: 5.7, bad: 6.5 },
  /** total cholesterol, mg/dL */
  cholesterol: { acceptable: 200, bad: 240 },
  /** resting pulse, bpm: good inside `good`, acceptable inside `acceptable`, otherwise bad */
  pulse: { good: [60, 100], acceptable: [50, 110] },
  /** cm, adults only (WHO waist circumference cut-offs): acceptable from, bad from (#56) */
  waistCircumference: { male: { acceptable: 94, bad: 102 }, female: { acceptable: 80, bad: 88 } },
  /** cm, adults only: bad from, otherwise good (#56) */
  neckCircumference: { male: { bad: 37 }, female: { bad: 34 } },
  /**
   * Fitness age against the real age, adults only (#56). Owner-approved display
   * rule (2026-10-05): good when not older than the real age, acceptable up to
   * `acceptableYearsOver` older, bad beyond.
   */
  fitnessAge: { acceptableYearsOver: 5 },
} as const;

/** Waist, neck and fitness age are judged from 18 (#56). */
export const ADULT_AGE_MONTHS = 18 * 12;

const BMI_LEVEL: Record<BmiCategory, Level> = {
  healthy: 'good',
  underweight: 'acceptable',
  overweight: 'acceptable',
  obese: 'bad',
  severe_obesity: 'bad',
};

export interface IndicatorReading {
  key: Indicator;
  /** Blood pressure as "systolic/diastolic"; every other indicator as a number. */
  value: number | string;
  /** kg/m², mmHg, %, mg/dL, bpm, cm (waist, neck) or years (fitness age). */
  unit: string;
  /**
   * null when the value is shown but not judged: a BMI under age 2, and a
   * child's (under 18) or a sex-unknown adult's waist or neck.
   */
  level: Level | null;
  bmiCategory?: BmiCategory | null;
  percentile?: number | null;
  /** Fitness age only: the real age in whole years it is compared with. */
  realAge?: number;
  /** Fitness age only: 'under' / 'over' when clipped to 20 / 75 (the value then is 20 / 75). */
  bound?: 'under' | 'over' | null;
}

export type ExamValues = Omit<ExamInput, 'date'> & {
  cholesterol?: unknown;
  pulse?: unknown;
  heartRate?: unknown;
  restingHeartRate?: unknown;
  waistCircumference?: unknown;
  neckCircumference?: unknown;
  activityFrequency?: unknown;
  activityDuration?: unknown;
  activityIntensity?: unknown;
};

/**
 * The measured indicators of one exam with their levels, in INDICATORS order.
 * Unmeasured ones (missing, empty or 0) are left out. `ageMonths` is the age at
 * the exam; without it adult ranges apply. Waist and neck are judged by sex
 * from 18 and shown without a level under 18 or without a sex; fitness age is
 * computed (never stored) for adults with a birth date, sex, waist, pulse and
 * the three activity answers, and left out otherwise (#56).
 */
export function readIndicators(
  exam: ExamValues,
  person?: { ageMonths: number | null; sex?: Sex | null }
): IndicatorReading[] {
  const ageMonths = person?.ageMonths ?? null;
  const out: IndicatorReading[] = [];

  const bmi = measured(exam.bmi);
  if (bmi !== null) {
    const { category, percentile } = bmiCategory(bmi, ageMonths, person?.sex);
    out.push({
      key: 'bmi',
      value: Math.round(bmi * 10) / 10,
      unit: 'kg/m²',
      level: category ? BMI_LEVEL[category] : null,
      bmiCategory: category,
      percentile,
    });
  }

  const s = measured(exam.bloodPressure?.systolic);
  const d = measured(exam.bloodPressure?.diastolic);
  if (s !== null || d !== null) {
    const { acceptable, bad } = INDICATOR_LIMITS.bloodPressure;
    const sys = s ?? 0;
    const dia = d ?? 0;
    const level: Level =
      sys >= bad.systolic || dia >= bad.diastolic ? 'bad' : sys >= acceptable.systolic || dia >= acceptable.diastolic ? 'acceptable' : 'good';
    out.push({ key: 'bloodPressure', value: `${s ?? '-'}/${d ?? '-'}`, unit: 'mmHg', level });
  }

  const a1c = measured(exam.hba1c);
  if (a1c !== null) {
    const { acceptable, bad } = INDICATOR_LIMITS.hba1c;
    out.push({ key: 'hba1c', value: a1c, unit: '%', level: a1c >= bad ? 'bad' : a1c >= acceptable ? 'acceptable' : 'good' });
  }

  const chol = measured(exam.cholesterol);
  if (chol !== null) {
    const { acceptable, bad } = INDICATOR_LIMITS.cholesterol;
    out.push({
      key: 'cholesterol',
      value: chol,
      unit: 'mg/dL',
      level: chol >= bad ? 'bad' : chol >= acceptable ? 'acceptable' : 'good',
    });
  }

  const pulse = measured(exam.pulse) ?? measured(exam.heartRate) ?? measured(exam.restingHeartRate);
  if (pulse !== null) {
    const { good, acceptable } = INDICATOR_LIMITS.pulse;
    const level: Level =
      pulse >= good[0] && pulse <= good[1] ? 'good' : pulse >= acceptable[0] && pulse <= acceptable[1] ? 'acceptable' : 'bad';
    out.push({ key: 'pulse', value: pulse, unit: 'bpm', level });
  }

  const sex = person?.sex === 'male' || person?.sex === 'female' ? person.sex : null;
  const adult = ageMonths === null || ageMonths >= ADULT_AGE_MONTHS;

  const waist = measured(exam.waistCircumference);
  if (waist !== null) {
    const limits = sex && adult ? INDICATOR_LIMITS.waistCircumference[sex] : null;
    const level: Level | null = limits ? (waist >= limits.bad ? 'bad' : waist >= limits.acceptable ? 'acceptable' : 'good') : null;
    out.push({ key: 'waistCircumference', value: waist, unit: 'cm', level });
  }

  const neck = measured(exam.neckCircumference);
  if (neck !== null) {
    const limits = sex && adult ? INDICATOR_LIMITS.neckCircumference[sex] : null;
    out.push({ key: 'neckCircumference', value: neck, unit: 'cm', level: limits ? (neck >= limits.bad ? 'bad' : 'good') : null });
  }

  if (ageMonths !== null && ageMonths >= ADULT_AGE_MONTHS) {
    const realAge = Math.floor(ageMonths / 12);
    const result = fitnessAge({
      sex,
      ageYears: realAge,
      waistCm: waist,
      restingHeartRate: pulse,
      frequency: exam.activityFrequency,
      duration: exam.activityDuration,
      intensity: exam.activityIntensity,
    });
    if (result) {
      const over = result.rawFitnessAge - realAge;
      const level: Level = over <= 0 ? 'good' : over <= INDICATOR_LIMITS.fitnessAge.acceptableYearsOver ? 'acceptable' : 'bad';
      out.push({ key: 'fitnessAge', value: result.fitnessAge, unit: 'years', level, realAge, bound: result.bound });
    }
  }
  return out;
}
